import { supabase } from './supabase';
import { validateDeclaration } from './intakePolicy';
import { getActiveChatLocale, hydrateChatLocale } from './chatI18n';
import { getConversionAttribution } from './growthIntel/tracker';

/**
 * Chat client library.
 *
 * Session model: the browser keeps an OPAQUE session token in sessionStorage
 * (tab-scoped, NOT localStorage) and sends it as the `X-Chat-Session` header on
 * every chat function call. The server stores only its SHA-256 hash.
 *
 * Only the last 20 messages are persisted locally — never resumes or files.
 */

const TOKEN_KEY = 'cendien_chat_token';
const MESSAGES_KEY = 'cendien_chat_messages';
const MAX_STORED_MESSAGES = 20;

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL || '';
const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY || '';

function fnUrl(slug: string): string {
  return `${SUPABASE_URL.replace(/\/+$/, '')}/functions/v1/${slug}`;
}

function baseHeaders(): Record<string, string> {
  return {
    apikey: SUPABASE_ANON_KEY,
    Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
    'Content-Type': 'application/json',
  };
}

/** Absolute URL of the static site-knowledge.json asset (honours the base path). */
export function getKnowledgeUrl(): string {
  try {
    const base = import.meta.env.BASE_URL || '/';
    const rel = `${base.replace(/\/+$/, '')}/site-knowledge.json`;
    return new URL(rel, window.location.origin).href;
  } catch {
    return '/site-knowledge.json';
  }
}

// ── Session token ───────────────────────────────────────────────────────────
export function getStoredToken(): string | null {
  try {
    return sessionStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

/** Client-observed, NON-security context sent once when a session is created. */
function sessionMetadata(): Record<string, unknown> {
  hydrateChatLocale();
  const locale = getActiveChatLocale();
  // The recorded consent-text version includes the locale (e.g. chat-v1-es) so
  // we can prove WHICH localized consent wording the visitor accepted.
  const out: Record<string, unknown> = { consent: true, consent_version: `chat-v1-${locale}`, locale };
  try { out.attribution = getConversionAttribution(); } catch { /* ignore */ }
  try { out.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { /* ignore */ }
  try { out.language = navigator.language; } catch { /* ignore */ }
  try { out.languages = Array.isArray(navigator.languages) ? navigator.languages.slice(0, 5) : undefined; } catch { /* ignore */ }
  try {
    out.entry_page = window.location.pathname + window.location.search;
    out.referrer = document.referrer ? document.referrer.slice(0, 300) : '';
    out.viewport = `${window.innerWidth}x${window.innerHeight}`;
    const params = new URLSearchParams(window.location.search);
    const utm: Record<string, string> = {};
    (['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const).forEach((k) => {
      const v = params.get(k);
      if (v) utm[k] = v.slice(0, 120);
    });
    if (Object.keys(utm).length) out.utm = utm;
  } catch { /* ignore */ }
  return out;
}

let sessionPromise: Promise<string | null> | null = null;

/** Returns an existing session token, or issues a fresh one. */
export async function ensureChatSession(): Promise<string | null> {
  const existing = getStoredToken();
  if (existing) return existing;
  if (sessionPromise) return sessionPromise;

  sessionPromise = (async () => {
    try {
      const res = await fetch(fnUrl('chat-session'), { method: 'POST', headers: baseHeaders(), body: JSON.stringify(sessionMetadata()) });
      const data = await res.json().catch(() => null);
      if (data && data.ok && data.token) {
        try {
          sessionStorage.setItem(TOKEN_KEY, data.token as string);
        } catch {
          /* ignore */
        }
        return data.token as string;
      }
      return null;
    } catch {
      return null;
    } finally {
      sessionPromise = null;
    }
  })();

  return sessionPromise;
}

/** Clears the session + local transcript (starts a brand-new conversation). */
export function resetChatSession(): void {
  try {
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(MESSAGES_KEY);
  } catch {
    /* ignore */
  }
}

// ── Local transcript (last 20 messages only) ─────────────────────────────────
export interface StoredMessage {
  role: 'user' | 'assistant';
  content: string;
  followups?: string[];
  feedback?: 'up' | 'down';
  feedbackNote?: string;
}

export function loadStoredMessages(): StoredMessage[] {
  try {
    const raw = sessionStorage.getItem(MESSAGES_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.slice(-MAX_STORED_MESSAGES);
  } catch {
    return [];
  }
}

export function saveStoredMessages(messages: StoredMessage[]): void {
  try {
    sessionStorage.setItem(MESSAGES_KEY, JSON.stringify(messages.slice(-MAX_STORED_MESSAGES)));
  } catch {
    /* ignore */
  }
}

// ── Attribution (page + utm_* only) ──────────────────────────────────────────
export interface Attribution {
  page: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
}

/** Page-context metadata for a file upload (page, sanitized referrer, UTM). */
export function attributionMeta(): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  try {
    out.page = window.location.pathname + window.location.search;
    out.referrer = document.referrer ? document.referrer.slice(0, 300) : '';
    const params = new URLSearchParams(window.location.search);
    const utm: Record<string, string> = {};
    (['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const).forEach((k) => {
      const v = params.get(k);
      if (v) utm[k] = v.slice(0, 120);
    });
    if (Object.keys(utm).length) out.utm = utm;
  } catch { /* ignore */ }
  try { out.attribution = getConversionAttribution(); } catch { /* ignore */ }
  return out;
}

export function getAttribution(): Attribution {
  const attr: Attribution = { page: '/' };
  try {
    attr.page = window.location.pathname + window.location.search;
    const params = new URLSearchParams(window.location.search);
    (['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const).forEach((k) => {
      const v = params.get(k);
      if (v) attr[k] = v.slice(0, 120);
    });
  } catch {
    /* ignore */
  }
  return attr;
}

// ── SSE streaming ───────────────────────────────────────────────────────────
export interface ReviewPayload {
  draft_id: string;
  kind: 'contact' | 'application' | 'general_interest';
  fields: Record<string, unknown>;
  job_id?: string | null;
}

export interface UploadRequestInfo {
  kind?: 'rfp' | 'resume';
  contact?: { name?: string; email?: string; company?: string; organization?: string };
}

export interface StreamHandlers {
  onDelta?: (text: string) => void;
  onReview?: (review: ReviewPayload) => void;
  onUploadRequest?: (info?: UploadRequestInfo) => void;
  onFollowups?: (questions: string[]) => void;
  onDone?: (info: { status: string; intent: string; sources: { url: string; title: string }[] }) => void;
  onError?: (message: string, opts?: { fallback?: boolean }) => void;
}

/**
 * Streams one message from the site-chat edge function (SSE). Dispatches typed
 * events to the provided handlers.
 */
export async function streamChat(
  message: string,
  opts: { page?: string; pageFollowups?: string[]; documentId?: string; resumeUploadId?: string; variant?: string | null },
  handlers: StreamHandlers,
): Promise<void> {
  const token = await ensureChatSession();
  if (!token) {
    handlers.onError?.('Our assistant is temporarily unavailable.', { fallback: true });
    return;
  }

  let res: Response;
  try {
    res = await fetch(fnUrl('site-chat'), {
      method: 'POST',
      headers: { ...baseHeaders(), 'X-Chat-Session': token },
      body: JSON.stringify({
        message,
        page: opts.page,
        knowledge_url: getKnowledgeUrl(),
        locale: getActiveChatLocale(),
        page_followups: Array.isArray(opts.pageFollowups) ? opts.pageFollowups.slice(0, 6) : [],
        document_id: opts.documentId || undefined,
        resume_upload_id: opts.resumeUploadId || undefined,
        variant: opts.variant || undefined,
      }),
    });
  } catch {
    handlers.onError?.('We could not reach our assistant. Please try again.', { fallback: true });
    return;
  }

  const contentType = res.headers.get('Content-Type') || '';
  if (!res.ok || !res.body || contentType.includes('application/json')) {
    let msg = 'Our assistant is temporarily unavailable.';
    let fallback = true;
    try {
      const data = await res.json();
      if (data?.error) msg = data.error as string;
      fallback = data?.fallback === true || data?.disabled === true || !data?.ok;
      // Session expired → drop the cached token so the next attempt re-issues one.
      if (res.status === 401) resetChatSession();
    } catch {
      /* keep defaults */
    }
    handlers.onError?.(msg, { fallback });
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let sawEvent = false;

  const dispatch = (block: string) => {
    for (const line of block.split('\n')) {
      const trimmed = line.trim();
      if (!trimmed.startsWith('data:')) continue;
      const payload = trimmed.slice(5).trim();
      if (!payload) continue;
      let evt: Record<string, unknown>;
      try {
        evt = JSON.parse(payload);
      } catch {
        continue;
      }
      sawEvent = true;
      switch (evt.type) {
        case 'delta':
          handlers.onDelta?.(String(evt.text || ''));
          break;
        case 'review':
          handlers.onReview?.({
            draft_id: String(evt.draft_id || ''),
            kind: (evt.kind as ReviewPayload['kind']) || 'contact',
            fields: (evt.fields as Record<string, unknown>) || {},
            job_id: (evt.job_id as string) || null,
          });
          break;
        case 'upload':
          handlers.onUploadRequest?.({
            kind: (evt.kind === 'rfp' ? 'rfp' : evt.kind === 'resume' ? 'resume' : undefined),
            contact: (evt.contact && typeof evt.contact === 'object') ? (evt.contact as UploadRequestInfo['contact']) : undefined,
          });
          break;
        case 'followups':
          handlers.onFollowups?.(
            Array.isArray(evt.questions)
              ? (evt.questions as unknown[]).map((q) => String(q)).filter((q) => q.trim().length > 0).slice(0, 3)
              : [],
          );
          break;
        case 'done':
          handlers.onDone?.({
            status: String(evt.status || 'open'),
            intent: String(evt.intent || 'general'),
            sources: Array.isArray(evt.sources) ? (evt.sources as { url: string; title: string }[]) : [],
          });
          break;
        case 'error':
          handlers.onError?.(String(evt.message || 'Something went wrong.'));
          break;
        default:
          break;
      }
    }
  };

  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      // eslint-disable-next-line no-await-in-loop
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const blocks = buffer.split('\n\n');
      buffer = blocks.pop() || '';
      blocks.forEach(dispatch);
    }
    if (buffer.trim()) dispatch(buffer);
  } catch {
    handlers.onError?.('The connection dropped. Please try again.');
  }

  if (!sawEvent) handlers.onError?.('Our assistant is temporarily unavailable.', { fallback: true });
}

// ── Intake submit (user button) ──────────────────────────────────────────────
export interface SubmitIntakeResult {
  ok: boolean;
  reference?: string;
  id?: string;
  error?: string;
}

export function newIdempotencyKey(): string {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch {
    /* ignore */
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export async function submitIntake(
  draftId: string,
  idempotencyKey: string,
  opts: { resumeUploadId?: string | null } = {},
): Promise<SubmitIntakeResult> {
  const token = await ensureChatSession();
  if (!token) return { ok: false, error: 'Session expired. Please start a new conversation.' };
  try {
    const res = await fetch(fnUrl('submit-intake'), {
      method: 'POST',
      headers: { ...baseHeaders(), 'X-Chat-Session': token },
      body: JSON.stringify({
        draft_id: draftId,
        idempotency_key: idempotencyKey,
        resume_upload_id: opts.resumeUploadId || null,
        attribution: getConversionAttribution(),
      }),
    });
    const data = await res.json().catch(() => null);
    if (!res.ok || !data || data.ok !== true) {
      return { ok: false, error: (data && (data.error as string)) || 'We could not submit that. Please try again.' };
    }
    return { ok: true, reference: data.reference as string, id: data.id as string };
  } catch {
    return { ok: false, error: 'We could not submit that. Please try again.' };
  }
}

// ── Resume upload (chat + careers) ───────────────────────────────────────────
// Format/size validation is delegated to the ONE shared policy in intakePolicy
// (PDF, DOC, DOCX, TXT, RTF · 10 MB) so the client can never drift from the
// server validators / bucket settings.

// ── Upload/request budgets (documented in docs/chat-agent.md) ──
// Pure values live in ./uploadBudgets so they can be unit-tested without
// pulling in the Supabase client.
import {
  UPLOAD_INACTIVITY_MS,
  REQUEST_DEADLINE_MS,
  STATUS_BACKOFF_MS,
  uploadTotalDeadlineMs,
} from './uploadBudgets';
export { UPLOAD_INACTIVITY_MS, REQUEST_DEADLINE_MS, STATUS_BACKOFF_MS, uploadTotalDeadlineMs };

export interface PreparedUpload {
  ok: boolean;
  uploadId?: string;
  path?: string;
  signedUrl?: string;
  error?: string;
}

export async function prepareResumeUpload(file: File, opts: { source?: 'chat' | 'careers_form' } = {}): Promise<PreparedUpload> {
  const check = validateDeclaration('recruiting', file.name, file.size);
  if (!check.ok) return { ok: false, error: check.error || 'Unsupported file.' };

  const token = await ensureChatSession();
  if (!token) return { ok: false, error: 'Session expired. Please try again.' };
  try {
    const res = await fetch(fnUrl('prepare-resume-upload'), {
      method: 'POST',
      headers: { ...baseHeaders(), 'X-Chat-Session': token },
      body: JSON.stringify({ filename: file.name, mime: file.type, size: file.size, source: opts.source || 'chat', attribution: getConversionAttribution() }),
      signal: AbortSignal.timeout(REQUEST_DEADLINE_MS),
    });
    const data = await res.json().catch(() => null);
    if (!res.ok || !data || data.ok !== true) return { ok: false, error: (data && (data.error as string)) || 'Could not prepare the upload.' };
    return { ok: true, uploadId: data.upload_id as string, path: data.path as string, signedUrl: data.signed_url as string };
  } catch {
    return { ok: false, error: 'Could not prepare the upload.' };
  }
}

/**
 * PUTs the file to the signed upload URL via XHR so we can report progress.
 *
 * Timeouts: an inactivity timer (no progress for UPLOAD_INACTIVITY_MS) plus a
 * total deadline scaled by size. ontimeout/onabort/onerror all settle the
 * promise; `cancel()` aborts the request immediately.
 */
export interface UploadHandle {
  promise: Promise<{ ok: boolean; aborted?: boolean; error?: string }>;
  cancel: () => void;
}

export function uploadToSignedUrl(signedUrl: string, file: File, onProgress?: (pct: number) => void): UploadHandle {
  const xhr = new XMLHttpRequest();
  let settled = false;
  let inactivityTimer: ReturnType<typeof setTimeout> | undefined;
  let totalTimer: ReturnType<typeof setTimeout> | undefined;
  const totalMs = uploadTotalDeadlineMs(file.size);

  const promise = new Promise<{ ok: boolean; aborted?: boolean; error?: string }>((resolve) => {
    const finish = (r: { ok: boolean; aborted?: boolean; error?: string }) => {
      if (settled) return;
      settled = true;
      if (inactivityTimer) clearTimeout(inactivityTimer);
      if (totalTimer) clearTimeout(totalTimer);
      resolve(r);
    };
    const resetInactivity = () => {
      if (inactivityTimer) clearTimeout(inactivityTimer);
      inactivityTimer = setTimeout(() => {
        try { xhr.abort(); } catch { /* ignore */ }
        finish({ ok: false, error: 'The upload stalled (no progress for 30 seconds). Please retry.' });
      }, UPLOAD_INACTIVITY_MS);
    };

    try {
      xhr.open('PUT', signedUrl, true);
      xhr.setRequestHeader('x-upsert', 'false');
      if (file.type) xhr.setRequestHeader('Content-Type', file.type);
      resetInactivity();
      totalTimer = setTimeout(() => {
        try { xhr.abort(); } catch { /* ignore */ }
        finish({ ok: false, error: 'The upload took too long and was cancelled. Please retry on a stronger connection.' });
      }, totalMs);
      xhr.upload.onprogress = (e) => {
        resetInactivity();
        if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) finish({ ok: true });
        else finish({ ok: false, error: `Upload failed (HTTP ${xhr.status}). Please retry.` });
      };
      xhr.onerror = () => finish({ ok: false, error: 'Upload failed — check your connection and retry.' });
      xhr.ontimeout = () => finish({ ok: false, error: 'The upload timed out. Please retry.' });
      xhr.onabort = () => finish({ ok: false, aborted: true, error: 'Upload cancelled.' });
      xhr.send(file);
    } catch (err) {
      finish({ ok: false, error: err instanceof Error ? err.message : 'Upload failed.' });
    }
  });

  return { promise, cancel: () => { try { xhr.abort(); } catch { /* ignore */ } } };
}

export interface FinalizeResult {
  ok: boolean;
  state?: string;
  status?: string;
  detectedType?: string;
  note?: string;
  applicationId?: string;
  reference?: string;
  duplicate?: boolean;
  notified?: boolean;
  needContact?: boolean;
  error?: string;
}

export async function finalizeResumeUpload(
  uploadId: string,
  opts: { meta?: Record<string, unknown>; uploadDurationMs?: number } = {},
): Promise<FinalizeResult> {
  const token = await ensureChatSession();
  if (!token) return { ok: false, error: 'Session expired. Please try again.' };
  try {
    const res = await fetch(fnUrl('finalize-resume-upload'), {
      method: 'POST',
      headers: { ...baseHeaders(), 'X-Chat-Session': token },
      body: JSON.stringify({ upload_id: uploadId, meta: opts.meta, upload_duration_ms: opts.uploadDurationMs }),
      signal: AbortSignal.timeout(REQUEST_DEADLINE_MS),
    });
    const data = await res.json().catch(() => null);
    if (!res.ok || !data || data.ok !== true) {
      return { ok: false, state: data?.state as string, error: (data && (data.error as string)) || 'The file could not be accepted.' };
    }
    return {
      ok: true,
      state: data.state as string,
      status: (data.status as string) || undefined,
      detectedType: data.detected_type as string,
      note: data.note as string,
      applicationId: (data.application_id as string) || undefined,
      reference: (data.reference as string) || undefined,
      duplicate: data.duplicate === true,
      notified: data.notified === true,
      needContact: data.need_contact === true,
    };
  } catch {
    return { ok: false, error: 'The file could not be checked. Please try again.' };
  }
}

// ── Upload receipt status (read-only, session-authorized) ────────────────────
export type ReceiptStatus =
  | 'saved'
  | 'scan_pending'
  | 'ready'
  | 'email_queued'
  | 'sent'
  | 'email_failed'
  | 'scan_failed'
  | 'rejected'
  | 'error';

export interface ContactSuggestion {
  full_name?: string | null;
  email?: string | null;
  phone?: string | null;
  confidence?: number | null;
  multiple?: boolean;
}

export interface UploadStatusResult {
  ok: boolean;
  status?: ReceiptStatus;
  state?: string;
  reference?: string;
  filename?: string;
  contactSuggestion?: ContactSuggestion | null;
  error?: string;
}

/** Read-only status poll — never calls a mutating endpoint. */
export async function getUploadStatus(uploadId: string): Promise<UploadStatusResult> {
  const token = await ensureChatSession();
  if (!token) return { ok: false, error: 'Session expired. Please start a new conversation.' };
  try {
    const res = await fetch(fnUrl('upload-status'), {
      method: 'POST',
      headers: { ...baseHeaders(), 'X-Chat-Session': token },
      body: JSON.stringify({ upload_id: uploadId }),
      signal: AbortSignal.timeout(REQUEST_DEADLINE_MS),
    });
    const data = await res.json().catch(() => null);
    if (!res.ok || !data || data.ok !== true) {
      return { ok: false, error: (data && (data.error as string)) || 'Could not check the file status.' };
    }
    return {
      ok: true,
      status: (data.status as ReceiptStatus) || undefined,
      state: (data.state as string) || undefined,
      reference: (data.reference as string) || undefined,
      filename: (data.filename as string) || undefined,
      contactSuggestion: (data.contact_suggestion as ContactSuggestion | null) || null,
    };
  } catch {
    return { ok: false, error: 'Could not check the file status.' };
  }
}

// ── Resume contact mini-form (chat) ──────────────────────────────────────────
export interface ResumeContactFields {
  full_name: string;
  email: string;
  phone?: string;
  job_title?: string;
  linkedin_url?: string;
  /** E.164 form from libphonenumber-js (validated on the client). */
  phone_e164?: string;
  /** Extension digits, preserved from the visitor's input. */
  phone_extension?: string;
  /** Where the confirmed values came from. */
  provenance?: 'manual' | 'extracted' | 'edited';
  marketing_opt_in?: boolean;
}

export interface ResumeContactResult {
  ok: boolean;
  reference?: string;
  notified?: boolean;
  error?: string;
}

/**
 * Confirms the visitor's contact details for a saved-but-unsubmitted resume.
 * THIS is what creates the job_applications row server-side (with the real
 * name + email) and links every resume upload from the session to it. Queues
 * the recruiting notification, which is sent once the file passes the scan.
 */
export async function submitResumeContact(uploadId: string, fields: ResumeContactFields): Promise<ResumeContactResult> {
  const token = await ensureChatSession();
  if (!token) return { ok: false, error: 'Session expired. Please try again.' };
  try {
    const res = await fetch(fnUrl('submit-resume-contact'), {
      method: 'POST',
      headers: { ...baseHeaders(), 'X-Chat-Session': token },
      body: JSON.stringify({
        upload_id: uploadId,
        full_name: fields.full_name,
        email: fields.email,
        phone: fields.phone || undefined,
        phone_e164: fields.phone_e164 || undefined,
        phone_extension: fields.phone_extension || undefined,
        provenance: fields.provenance || undefined,
        marketing_opt_in: fields.marketing_opt_in === true,
        job_title: fields.job_title || undefined,
        linkedin_url: fields.linkedin_url || undefined,
      }),
    });
    const data = await res.json().catch(() => null);
    if (!res.ok || !data || data.ok !== true) {
      return { ok: false, error: (data && (data.error as string)) || 'We could not save your details. Please try again.' };
    }
    return { ok: true, reference: data.reference as string, notified: data.notified === true };
  } catch {
    return { ok: false, error: 'We could not save your details. Please try again.' };
  }
}

// ── Multi-file submission review ("Send documents to Cendien") ───────────────
export interface SubmissionFields {
  full_name: string;
  email: string;
  phone?: string;
  phone_e164?: string;
  phone_extension?: string;
  company?: string;
  purpose?: string;
  message?: string;
  provenance?: 'manual' | 'extracted' | 'edited';
  marketing_opt_in?: boolean;
}

export interface SubmitDocumentsResult {
  ok: boolean;
  reference?: string;
  submissionId?: string;
  destinationEmail?: string;
  error?: string;
}

/** Destination shown in the review step (server resolves the authoritative value). */
export function destinationFor(category: 'recruiting' | 'solicitation'): string {
  return category === 'solicitation' ? 'solicitations@cendien.com' : 'resumes@cendien.com';
}

/**
 * The review step's final submit. Session-scoped; called by the visitor's
 * explicit button so it keeps working with the LLM down. Nothing is emailed
 * here — the durable worker dispatches once files are clean.
 */
export async function submitDocuments(
  category: 'recruiting' | 'solicitation',
  fields: SubmissionFields,
  uploadIds: string[],
): Promise<SubmitDocumentsResult> {
  const token = await ensureChatSession();
  if (!token) return { ok: false, error: 'Session expired. Please start a new conversation.' };
  try {
    const res = await fetch(fnUrl('submit-documents'), {
      method: 'POST',
      headers: { ...baseHeaders(), 'X-Chat-Session': token },
      body: JSON.stringify({ category, fields, upload_ids: uploadIds, attribution: getConversionAttribution() }),
      signal: AbortSignal.timeout(REQUEST_DEADLINE_MS),
    });
    const data = await res.json().catch(() => null);
    if (!res.ok || !data || data.ok !== true) {
      return { ok: false, error: (data && (data.error as string)) || 'We could not send your documents. Please try again.' };
    }
    return {
      ok: true,
      reference: data.reference as string,
      submissionId: data.submission_id as string,
      destinationEmail: data.destination_email as string,
    };
  } catch {
    return { ok: false, error: 'We could not send your documents. Please try again.' };
  }
}

// ── RFP / bid-document upload (chat) ─────────────────────────────────────────
// Format/size validation is delegated to the ONE shared policy in intakePolicy
// (PDF, DOC, DOCX, XLS, XLSX, CSV, PPTX, TXT · 20 MB).
export interface RfpContact { name?: string; email?: string; company?: string; organization?: string; }

export async function prepareRfpUpload(file: File, contact: RfpContact = {}): Promise<PreparedUpload> {
  const check = validateDeclaration('solicitation', file.name, file.size);
  if (!check.ok) return { ok: false, error: check.error || 'Unsupported file.' };

  const token = await ensureChatSession();
  if (!token) return { ok: false, error: 'Session expired. Please try again.' };
  try {
    const res = await fetch(fnUrl('prepare-rfp-upload'), {
      method: 'POST',
      headers: { ...baseHeaders(), 'X-Chat-Session': token },
      body: JSON.stringify({
        filename: file.name, mime: file.type, size: file.size,
        contact_name: contact.name || undefined, contact_email: contact.email || undefined,
        contact_company: contact.company || undefined, contact_organization: contact.organization || undefined,
        attribution: getConversionAttribution(),
      }),
      signal: AbortSignal.timeout(REQUEST_DEADLINE_MS),
    });
    const data = await res.json().catch(() => null);
    if (!res.ok || !data || data.ok !== true) return { ok: false, error: (data && (data.error as string)) || 'Could not prepare the upload.' };
    return { ok: true, uploadId: data.upload_id as string, path: data.path as string, signedUrl: data.signed_url as string };
  } catch {
    return { ok: false, error: 'Could not prepare the upload.' };
  }
}

export interface RfpAnalysis {
  summary: string;
  key_requirements: string[];
  deadline: string | null;
  scope: string;
  questions: string[];
  manual_review?: boolean;
  model?: string;
}

export interface RfpFinalizeResult {
  ok: boolean;
  state?: string;
  detectedType?: string;
  filename?: string;
  reference?: string | null;
  analysis?: RfpAnalysis;
  error?: string;
}

export async function finalizeRfpUpload(uploadId: string): Promise<RfpFinalizeResult> {
  const token = await ensureChatSession();
  if (!token) return { ok: false, error: 'Session expired. Please try again.' };
  try {
    const res = await fetch(fnUrl('finalize-rfp-upload'), {
      method: 'POST',
      headers: { ...baseHeaders(), 'X-Chat-Session': token },
      body: JSON.stringify({ upload_id: uploadId }),
      signal: AbortSignal.timeout(REQUEST_DEADLINE_MS),
    });
    const data = await res.json().catch(() => null);
    if (!res.ok || !data || data.ok !== true) {
      return { ok: false, state: data?.state as string, error: (data && (data.error as string)) || 'The document could not be accepted.' };
    }
    return {
      ok: true, state: data.state as string, detectedType: data.detected_type as string,
      filename: data.filename as string, reference: (data.reference as string) ?? null, analysis: data.analysis as RfpAnalysis,
    };
  } catch {
    return { ok: false, error: 'The document could not be checked. Please try again.' };
  }
}

/** Admin-only: short-lived signed URL for an uploaded document. */
export async function getDocumentLink(uploadId: string): Promise<{ ok: boolean; url?: string; filename?: string; error?: string }> {
  try {
    const res = await fetch(fnUrl('get-document-link'), { method: 'POST', headers: adminHeaders(), body: JSON.stringify({ upload_id: uploadId }) });
    const data = await res.json().catch(() => null);
    if (!res.ok || !data || data.ok !== true) return { ok: false, error: (data && (data.error as string)) || 'Could not create a download link.' };
    return { ok: true, url: data.url as string, filename: data.filename as string };
  } catch {
    return { ok: false, error: 'Could not create a download link.' };
  }
}

// ── Recruiter helpers (admin Candidates tab) ─────────────────────────────────
function adminHeaders(): Record<string, string> {
  const token = (typeof window !== 'undefined' && window.getCendienToken) ? window.getCendienToken() : '';
  return { ...baseHeaders(), Authorization: `Bearer ${token || SUPABASE_ANON_KEY}` };
}

export async function getResumeLink(
  uploadId: string,
  opts: { quarantined?: boolean } = {},
): Promise<{ ok: boolean; url?: string; error?: string; quarantined?: boolean }> {
  try {
    const res = await fetch(fnUrl('get-resume-link'), {
      method: 'POST',
      headers: adminHeaders(),
      body: JSON.stringify({ upload_id: uploadId, quarantined: opts.quarantined === true }),
    });
    const data = await res.json().catch(() => null);
    if (!res.ok || !data || data.ok !== true) return { ok: false, error: (data && (data.error as string)) || 'Could not create a download link.' };
    return { ok: true, url: data.url as string, quarantined: data.quarantined === true };
  } catch {
    return { ok: false, error: 'Could not create a download link.' };
  }
}

export async function releaseResume(uploadId: string, confirmed: boolean): Promise<{ ok: boolean; error?: string }> {
  try {
    const res = await fetch(fnUrl('release-resume'), { method: 'POST', headers: adminHeaders(), body: JSON.stringify({ upload_id: uploadId, confirmed }) });
    const data = await res.json().catch(() => null);
    if (!res.ok || !data || data.ok !== true) return { ok: false, error: (data && (data.error as string)) || 'Could not release the file.' };
    return { ok: true };
  } catch {
    return { ok: false, error: 'Could not release the file.' };
  }
}

export async function updateUploadState(uploadId: string, state: string): Promise<void> {
  try {
    await supabase.from('resume_uploads').update({ state }).eq('id', uploadId);
  } catch {
    /* best-effort */
  }
}

// ── Admin: force the security scan for one submission now ───────────────────
export async function scanSubmissionNow(submissionId: string): Promise<{ ok: boolean; error?: string }> {
  try {
    const res = await fetch(fnUrl('admin-scan-now'), {
      method: 'POST',
      headers: adminHeaders(),
      body: JSON.stringify({ submission_id: submissionId }),
    });
    const data = await res.json().catch(() => null);
    if (!res.ok || !data || data.ok !== true) {
      return { ok: false, error: (data && (data.error as string)) || 'Could not start the scan.' };
    }
    return { ok: true };
  } catch {
    return { ok: false, error: 'Could not start the scan.' };
  }
}

// ── Admin: audited email retry (per recipient/part) ─────────────────────────
export async function adminRetryEmail(messageId: string): Promise<{ ok: boolean; error?: string }> {
  try {
    const res = await fetch(fnUrl('admin-email-retry'), {
      method: 'POST',
      headers: adminHeaders(),
      body: JSON.stringify({ message_id: messageId }),
    });
    const data = await res.json().catch(() => null);
    if (!res.ok || !data || data.ok !== true) {
      return { ok: false, error: (data && (data.error as string)) || 'The retry could not be started.' };
    }
    return { ok: true };
  } catch {
    return { ok: false, error: 'The retry could not be started.' };
  }
}

// ── Admin: on-demand "Translate to English" (cached, non-destructive) ────────
export interface TranslateMessage { ts: string; text: string; }

export async function adminTranslateConversation(
  conversationId: string,
  messages: TranslateMessage[],
  targetLang = 'en',
): Promise<{ ok: boolean; translations?: Record<string, string>; error?: string }> {
  try {
    const res = await fetch(fnUrl('admin-translate'), {
      method: 'POST',
      headers: adminHeaders(),
      body: JSON.stringify({ conversation_id: conversationId, target_lang: targetLang, messages }),
    });
    const data = await res.json().catch(() => null);
    if (!res.ok || !data || data.ok !== true) {
      return { ok: false, error: (data && (data.error as string)) || 'Translation failed.' };
    }
    return { ok: true, translations: (data.translations as Record<string, string>) || {} };
  } catch {
    return { ok: false, error: 'Translation failed.' };
  }
}

// ── Helpful feedback (thumbs up/down) + "what was missing?" note ─────────────
export async function submitFeedback(helpful: boolean, note?: string): Promise<{ ok: boolean }> {
  const token = await ensureChatSession();
  if (!token) return { ok: false };
  try {
    const payload: Record<string, unknown> = { helpful };
    if (typeof note === 'string' && note.trim()) payload.note = note.trim().slice(0, 500);
    const res = await fetch(fnUrl('chat-feedback'), {
      method: 'POST',
      headers: { ...baseHeaders(), 'X-Chat-Session': token },
      body: JSON.stringify(payload),
    });
    const data = await res.json().catch(() => null);
    return { ok: res.ok && data?.ok === true };
  } catch {
    return { ok: false };
  }
}

// ── Follow-up chip click tracking ────────────────────────────────────────────
export async function trackFollowupClick(question: string, page?: string): Promise<void> {
  const q = (question || '').trim().slice(0, 200);
  if (q.length < 3) return;
  const token = await ensureChatSession();
  if (!token) return;
  try {
    await fetch(fnUrl('chat-followup-click'), {
      method: 'POST',
      headers: { ...baseHeaders(), 'X-Chat-Session': token },
      body: JSON.stringify({ question: q, page: page || undefined }),
    });
  } catch {
    /* best-effort — never block sending the question */
  }
}

// ── Open-widget bridge ───────────────────────────────────────────────────────
export const OPEN_CHAT_EVENT = 'cendien:open-chat';

export function openChat(message?: string): void {
  try {
    window.dispatchEvent(new CustomEvent(OPEN_CHAT_EVENT, { detail: { message } }));
  } catch {
    /* ignore */
  }
}